<template>
  <div class="bg-gray-50 shadow px-4 py-5 sm:rounded sm:p-6 h-full">
    <div class="bg-gray-50 shadow px-4 py-5 sm:rounded sm:p-6 h-full">
      <form
        @submit.prevent="Submit"
        novalidate
        class="space-y-8 divide-y divide-gray-200"
      >
        <div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Create an Authorization Request.
              </h3>
              <p class="mt-1 max-w-2xl text-sm text-gray-500">
                Please check back soon for the status of the request. You will be informed via email as well. Authorization Requests cannot be made on matters that are designated with a plan class of REF (Referral only). If you do not know if the matter is a REF plan class, you can refer to the Case page, which will provide you with the plan class designation on that case. This information was also provided to you via the Assigned to Attorney email that you received on the case.
              </p>
            </div>

            <div class="mt-6 sm:mt-5 space-y-6 sm:space-y-5">
              <div
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  for="matter_case"
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  Select Matter Case <span class="text-red-700">*</span>
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2">
                  <AutoComplete
                  class="h-10 pt-1 w-80"
                  forceSelection
                  :dropdown="true"
                  :class="{ 'border-red-400': v$.form.matter_id.$error }"
                  v-model="form.matter_id"
                  :suggestions="matterList"
                  @complete="searchAutocomplete($event, 'matterList')"
                  field="case"
                  inputId="matter_case"
                  aria-required="true"
                  :aria-invalid="v$.form.matter_id.$error ? 'true' : 'false'"
                  :aria-describedby="v$.form.matter_id.$error ? 'matter_case-error' : null"
                />
                  <div
                    class="text-red-600 mt-1 text-sm"
                    v-for="(error, index) in v$.form.matter_id.$errors"
                    :key="index"
                  >
                    <div
                      v-if="error.$validator == 'required'"
                      id="matter_case-error"
                      role="alert"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  for="subject"
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  Enter Subject <span class="text-red-700">*</span>
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="subject"
                    id="subject"
                    v-bind:value="form.subject"
                    :class="{ 'border-red-400': v$.form.subject.$error }"
                    :required="true"
                    :aria-invalid="v$.form.subject.$error ? 'true' : 'false'"
                    :aria-describedby="v$.form.subject.$error ? 'subject-error' : null"
                    v-on:keyup="form.subject = $event.target['value']"
                    class="max-w-lg block w-full shadow-sm border-barley sm:max-w-xs sm:text-sm rounded-md"
                  />
                  <div
                    class="text-red-600 mt-1 text-sm"
                    v-for="(error, index) in v$.form.subject.$errors"
                    :key="index"
                  >
                    <div
                      v-if="error.$validator == 'required'"
                      id="subject-error"
                      role="alert"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  for="description"
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  Description <span class="text-red-700">*</span>
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2">
                  <textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    class="max-w-lg shadow-sm block w-full border-barley sm:text-sm border rounded-md"
                    :class="{ 'border-red-400': v$.form.description.$error }"
                    required
                    aria-required="true"
                    :aria-invalid="v$.form.description.$error ? 'true' : 'false'"
                    :aria-describedby="
                      v$.form.description.$error
                        ? 'description-error'
                        : 'description-help'
                    "
                  ></textarea>
                  <p id="description-help" class="mt-1 text-xs text-gray-500">
                    Write a few sentences about your request.
                  </p>
                  <div
                    class="text-red-600 mt-1 text-sm"
                    v-for="(error, index) in v$.form.description.$errors"
                    :key="index"
                  >
                    <div
                      v-if="error.$validator == 'required'"
                      id="description-error"
                      role="alert"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  for="cover-photo"
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  Upload Attachments
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2">
                  <div
                    class="max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md"
                  >
                    <div class="space-y-1 text-center">
                      <svg
                        class="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <div class="flex text-sm text-gray-600">
                        <label
                          for="file-upload"
                          class="relative cursor-pointer bg-white rounded-md font-medium cobalt-text-color hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                        >
                          <span class="text-red-700">Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            @change="uploadFile"
                            type="file"
                            class="sr-only"
                          />
                        </label>
                        <p class="pl-1">or drag and drop</p>
                      </div>
                    </div>
                  </div>
                  <div
                    class="sm:col-span-2 mt-2"
                    v-if="form?.files?.length > 0"
                  >
                    <dt class="text-sm font-medium text-gray-500">
                      Attachments
                    </dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      <ul
                        role="list"
                        class="border border-gray-200 rounded-md divide-y divide-gray-200"
                      >
                        <li
                          class="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                          v-for="(file, index) in form?.files"
                          :key="index"
                        >
                          <div class="w-0 flex-1 flex items-center">
                            <svg
                              class="flex-shrink-0 h-5 w-5 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            <span class="ml-2 flex-1 w-0 truncate">
                              {{ file.name }}
                            </span>
                          </div>
                          <div class="ml-4 flex-shrink-0">
                            <a
                              href="javascript:void(0);"
                              @click="deleteFile(file)"
                              class="font-medium text-red-600 hover:text-red-500"
                              >Delete</a
                            >
                            |
                            <a
                              :href="
                                API_BASE +
                                  'downloadTempFile.php?name=' +
                                  file.name +
                                  '&attorney_number=' +
                                  att_user?.data?.id +
                                  '&module=request'
                              "
                              class="font-medium text-indigo-600 hover:text-indigo-500"
                            >
                              Download
                            </a>
                          </div>
                        </li>
                      </ul>
                    </dd>
                  </div>
                </div>
              </div>
            </div>
        </div>

        <div class="pt-5">
          <div class="flex justify-left">
            <button
              type="submit"
              class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white cobalt-button-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Create Request
            </button>

            <div class="sm:mt-0 ml-3">
              <router-link
                to="/authorizations"
                type="button"
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </router-link>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import useVuelidate from "@vuelidate/core"; // For validation
import { required } from "@vuelidate/validators"; // For validation
import $ from "jquery"; // To use jquery
import Constants from "../../constants.ts"; // importing constant resubale variables.

export default {
  name: "CreateAuth",
  setup() {
    return { v$: useVuelidate() }; //creating object to access validation object.
  },
  data() {
    return {
      API_BASE: Constants.API_BASE,
      matterList: [],
      form: {
        matter_id: "",
        subject: "",
        description: "",
        files: [],
      },
    };
  },
  validations() {
    //For Dynamic Validation based on Dynamic form fields
    return {
      form: {
        matter_id: { required },
        subject: { required },
        description: { required },
      },
    };
  },
  computed: mapState(["matter_cases", "att_user"]),
  mounted() {
    this.$store.dispatch("getMatterCases");
  },
  components: {
    //TextInput,
  },
  created() {
    this.$store.watch(
      () => this.$store.state.attorney_files,
      (value) => {
        if (value != "") {
          this.listingUI(value);
        }
      }
    );
    this.$store.watch(
      () => this.$store.state.is_uploaded,
      (value) => {
        if (value == true) {
          this.$toast.success("File uploaded successfully");
          this.show_upload();
        }
      }
    );
    this.$store.watch(
      () => this.$store.state.errors,
      (value) => {
        if (value != null) {
          if (value.message == "Authorization failed") {
            this.$toast.error(
              "Authorization Token Expired, Please login again!"
            );
            this.$store.dispatch("logout");
            setTimeout(() => {
              this.$router.push({ name: "Login" });
            }, 500);
          } else if (value.length > 0) {
            this.$toast.error("Something went wrong, please try again!");
          }
        }
      }
    );
  },
  methods: {
    searchAutocomplete: function(event, field) {
      if (field == "matterList") {
        if (!event.query.trim().length) {
          this.matterList = [...this.matter_cases];
        } else {
          this.matterList = this.matter_cases.filter((item) => {
            return item.case
              .toLowerCase()
              .includes(event.query.toLowerCase());
          });
        }
      }
    },
    Submit: function(e) {
      this.v$.form.$touch();
      if (this.v$.form.$pending || this.v$.form.$error) {
        this.$toast.error("Please fill all required fields");
        return;
      }
      this.form.matter_id=this.form.matter_id.matter_id;
      console.log(this.form.matter_id)
      this.$store
        .dispatch("addAuthRequest", this.form)
        .then((result) => {
          if (result.data.status == "success") {
            this.$toast.success(result.data.message);
            this.$router.push("authorizations");
          }
        })
        .catch((error) => {
          if (error.status == "Failed") {
            this.$toast.error(
              "Authorization Token Expired, Please login again!"
            );
            this.$store.dispatch("logout");
            this.$router.push("login");
          } else {
            this.$toast.error("Something went wrong, please try again!");
          }
          console.log("error: ", error);
        });

      e.preventDefault();
    },
    deleteFile: function(file) {
      this.$store.dispatch("deleteTempFile", { file: file }).then(
        (response) => {
          console.log(response);
          setTimeout(() => {
            if (this.$store.state.errors.length <= 0) {
              this.$toast.success("File deleted successfully");
              this.show_upload();
            }
          }, 1000);
        },
        (error) => {
          console.error("error from response", error);
        }
      );
    },
    show_upload: function() {
      this.$store.dispatch("getTempFilesList", {
        attorney_number: this.att_user?.data.id,
        module: "request",
      });
    },
    listingUI: function(data) {
      this.form.files = data.data;
    },
    uploadFile: function() {
      this.$store.dispatch("resetUploadFlag");
      var filename = $("#file-upload").val();
      if (filename.indexOf("\\") > -1) {
        filename = filename.split("\\").pop();
      }
      if (
        filename.substr(filename.length - 4) == ".pdf" ||
        filename.substr(filename.length - 4) == ".png" ||
        filename.substr(filename.length - 4) == ".jpg" ||
        filename.substr(filename.length - 4) == ".jpeg" ||
        filename.substr(filename.length - 4) == ".gif"
      ) {
        //
      } else {
        this.$toast.error("Only image and pdf allowed");
        return false;
      }
      for (let i = 0; i < this.form.files.length; i++) {
        if (this.form.files[i].name == filename) {
          this.$toast.error("Duplicate file can not be allowed.");
          return false;
        }
      }
      if (filename == "") {
        this.$toast.error("Please specify a file to import.");
        return false;
      }
      const formData = new FormData();
      var file = $("#file-upload")[0].files[0];
      formData.append("file", file);
      formData.append("attorney_number", this.att_user?.data?.id);
      formData.append("module", "request");
      this.$store.dispatch("uploadTempFile", formData);
    },
  },
};
</script>
